import React from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography
} from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import ShoppingCartIcon from '@material-ui/icons/ShoppingCart';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import RocketLaunchIcon from '@material-ui/icons/FlightTakeoff';

const useStyles = makeStyles((theme) => ({
  dialog: {
    '& .MuiDialog-paper': {
      borderRadius: theme.spacing(2),
      maxWidth: 600,
      padding: theme.spacing(2)
    }
  },
  dialogTitle: {
    textAlign: 'center',
    paddingTop: theme.spacing(3),
    paddingBottom: theme.spacing(2)
  },
  titleIcon: {
    fontSize: 80,
    color: theme.palette.primary.main,
    marginBottom: theme.spacing(2),
    animation: '$float 3s ease-in-out infinite'
  },
  title: {
    fontWeight: 700,
    fontSize: '1.8rem',
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(1)
  },
  subtitle: {
    color: theme.palette.text.secondary,
    fontSize: '1rem',
    marginTop: theme.spacing(1)
  },
  content: {
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(3)
  },
  jobTitle: {
    fontWeight: 600,
    fontSize: '1.2rem',
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(3),
    textAlign: 'center',
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.default,
    borderRadius: theme.spacing(1),
    border: `2px dashed ${theme.palette.primary.main}40`
  },
  priceBox: {
    textAlign: 'center',
    marginBottom: theme.spacing(3),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.primary.main + '15',
    borderRadius: theme.spacing(1),
    border: `2px solid ${theme.palette.primary.main}`
  },
  priceLabel: {
    fontSize: '0.9rem',
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(0.5)
  },
  price: {
    fontSize: '2.5rem',
    fontWeight: 700,
    color: theme.palette.primary.main
  },
  priceNote: {
    fontSize: '0.85rem',
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(0.5),
    fontStyle: 'italic'
  },
  benefitsList: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2)
  },
  benefitItem: {
    padding: theme.spacing(1, 0)
  },
  benefitIcon: {
    color: theme.palette.success.main,
    minWidth: 40
  },
  benefitText: {
    '& .MuiListItemText-primary': {
      fontSize: '1rem',
      fontWeight: 500,
      color: theme.palette.text.primary
    }
  },
  divider: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2)
  },
  actions: {
    padding: theme.spacing(2, 3, 3, 3),
    justifyContent: 'center',
    gap: theme.spacing(2),
    flexDirection: 'column'
  },
  buyButton: {
    padding: theme.spacing(1.5, 4),
    fontSize: '1.1rem',
    fontWeight: 600,
    textTransform: 'none',
    boxShadow: '0 4px 14px rgba(0,118,255,0.39)',
    borderRadius: theme.spacing(1),
    minWidth: 250,
    '&:hover': {
      boxShadow: '0 6px 20px rgba(0,118,255,0.5)',
      transform: 'translateY(-2px)'
    },
    transition: 'all 0.3s ease'
  },
  laterButton: {
    padding: theme.spacing(1, 3),
    fontSize: '0.95rem',
    textTransform: 'none',
    color: theme.palette.text.secondary,
    '&:hover': {
      backgroundColor: theme.palette.action.hover
    }
  },
  '@keyframes float': {
    '0%, 100%': {
      transform: 'translateY(0px)'
    },
    '50%': {
      transform: 'translateY(-10px)'
    }
  }
}));

const PaymentRequiredDialog = ({ open, onClose, onBuyPlan, job }) => {
  const classes = useStyles();

  const benefits = [
    'Drag-and-drop candidate pipeline (Kanban-style)',
    'Interview scheduling with automatic notifications',
    'Direct candidate messaging',
    'Toggle your job on/off anytime after activation',
    'One-time payment, no subscriptions'
  ];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      className={classes.dialog}
      maxWidth="sm"
      fullWidth>
      <DialogTitle className={classes.dialogTitle} disableTypography>
        <RocketLaunchIcon className={classes.titleIcon} />
        <Typography variant="h4" className={classes.title} component="div">
          Ready to Activate Your Job Post?
        </Typography>
        <Typography className={classes.subtitle}>
          One payment. Complete hiring workspace included.
        </Typography>
      </DialogTitle>

      <DialogContent className={classes.content}>
        {job && (
          <Box className={classes.jobTitle}>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              Job to Activate:
            </Typography>
            <Typography variant="h5">{job.title}</Typography>
          </Box>
        )}

        <Box className={classes.priceBox}>
          <Typography className={classes.priceLabel}>Starting at</Typography>
          <Typography className={classes.price}>$450</Typography>
          <Typography className={classes.priceNote}>
            One-time payment per job post. No subscriptions.
          </Typography>
        </Box>

        <Divider className={classes.divider} />

        <Typography
          variant="body1"
          gutterBottom
          style={{ fontWeight: 600, marginBottom: 16 }}>
          What You'll Get:
        </Typography>

        <List className={classes.benefitsList} dense>
          {benefits.map((benefit, index) => (
            <ListItem key={index} className={classes.benefitItem}>
              <ListItemIcon className={classes.benefitIcon}>
                <CheckCircleIcon />
              </ListItemIcon>
              <ListItemText primary={benefit} className={classes.benefitText} />
            </ListItem>
          ))}
        </List>
      </DialogContent>

      <DialogActions className={classes.actions}>
        <Button
          variant="contained"
          color="primary"
          className={classes.buyButton}
          onClick={onBuyPlan}
          startIcon={<ShoppingCartIcon />}>
          Choose Your Plan
        </Button>
        <Button
          variant="text"
          className={classes.laterButton}
          onClick={onClose}>
          I'll do this later
        </Button>
      </DialogActions>
    </Dialog>
  );
};

PaymentRequiredDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onBuyPlan: PropTypes.func.isRequired,
  job: PropTypes.shape({
    id: PropTypes.string,
    title: PropTypes.string
  })
};

PaymentRequiredDialog.defaultProps = {
  job: null
};

export default PaymentRequiredDialog;
